import { useEffect, useState } from "react";

const CHANNEL_NAME = "wvcea-attendee-state-v1";
const STORAGE_KEY = "wvcea:attendee-state-epoch:v1";
const LOCAL_EVENT = "wvcea:attendee-state-changed";
const NATIVE_EVENT = "wvcea:native-foreground";

export type AttendeeStateReason = "foreground" | "write" | "signed-out" | "account-deleted";
export interface AttendeeRunToken { scope: string; generation: number }

export function attendeeRunIsCurrent(captured: AttendeeRunToken, current: AttendeeRunToken): boolean {
  return captured.scope === current.scope && captured.generation === current.generation;
}

export function attendeeStateClearsPrivateState(reason: AttendeeStateReason): boolean {
  return reason === "signed-out" || reason === "account-deleted";
}

/** One signal, with no attendee data, tells every open view to ask its own trusted endpoint for a fresh copy. */
export function subscribeAttendeeState(listener: (reason: AttendeeStateReason) => void): () => void {
  let timer: number | null = null;
  const notify = (reason: AttendeeStateReason) => {
    if (attendeeStateClearsPrivateState(reason)) {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      listener(reason);
      return;
    }
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => listener(reason), 150);
  };
  const foreground = () => notify("foreground");
  const visible = () => { if (document.visibilityState === "visible") foreground(); };
  const local = (event: Event) => notify((event as CustomEvent<AttendeeStateReason>).detail ?? "write");
  const stored = (event: StorageEvent) => { if (event.key === STORAGE_KEY) notify(readReason(event.newValue)); };
  const channel = "BroadcastChannel" in globalThis ? new BroadcastChannel(CHANNEL_NAME) : null;
  if (channel) channel.onmessage = (event: MessageEvent<AttendeeStateReason>) => notify(event.data ?? "write");

  window.addEventListener("focus", foreground);
  window.addEventListener("pageshow", foreground);
  window.addEventListener("online", foreground);
  window.addEventListener("visibilitychange", visible);
  window.addEventListener(NATIVE_EVENT, foreground);
  window.addEventListener(LOCAL_EVENT, local);
  window.addEventListener("storage", stored);
  return () => {
    if (timer !== null) window.clearTimeout(timer);
    channel?.close();
    window.removeEventListener("focus", foreground);
    window.removeEventListener("pageshow", foreground);
    window.removeEventListener("online", foreground);
    window.removeEventListener("visibilitychange", visible);
    window.removeEventListener(NATIVE_EVENT, foreground);
    window.removeEventListener(LOCAL_EVENT, local);
    window.removeEventListener("storage", stored);
  };
}

export function emitAttendeeStateChanged(reason: Exclude<AttendeeStateReason, "foreground">): void {
  const value = `${reason}:${Date.now()}:${crypto.randomUUID()}`;
  try { localStorage.setItem(STORAGE_KEY, value); } catch { /* The local event still reaches this view. */ }
  window.dispatchEvent(new CustomEvent<AttendeeStateReason>(LOCAL_EVENT, { detail: reason }));
  if ("BroadcastChannel" in globalThis) {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(reason);
    channel.close();
  }
  if (attendeeStateClearsPrivateState(reason)) notifyNativeAccountEnded();
}

export function useAttendeeStateEpoch(): { epoch: number; reason: AttendeeStateReason } {
  const [signal, setSignal] = useState<{ epoch: number; reason: AttendeeStateReason }>({ epoch: 0, reason: "foreground" });
  useEffect(() => subscribeAttendeeState((reason) => setSignal((current) => ({ epoch: current.epoch + 1, reason }))), []);
  return signal;
}

function readReason(value: string | null): AttendeeStateReason {
  const reason = value?.split(":", 1)[0];
  return reason === "signed-out" || reason === "account-deleted" || reason === "write" ? reason : "foreground";
}

function notifyNativeAccountEnded(): void {
  const nativeWindow = window as Window & {
    webkit?: { messageHandlers?: { VerityConnectAccount?: { postMessage(value: string): void } } };
    VerityConnectAccount?: { ended(): void };
  };
  nativeWindow.webkit?.messageHandlers?.VerityConnectAccount?.postMessage("ended");
  nativeWindow.VerityConnectAccount?.ended();
}
